import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useSearchParams, Link } from "react-router-dom";
import draftToHtml from "draftjs-to-html";
import {
  IoArrowBackCircleOutline,
  IoArrowForwardCircleOutline,
  IoDesktopOutline,
  IoChevronDownOutline,
  IoChevronUpOutline,
  IoMusicalNotesSharp,
  IoChatbubbleEllipsesOutline,
  IoCopyOutline,
  IoShareSocialOutline
} from "react-icons/io5";
import LeftSidebar from "../../components/layout/LeftSidebar/LeftSidebar";
import LibraryIntro from "./LibraryIntro";
import RightSidebar from "../../components/layout/RightSidebar/RightSidebar";
import SongFullScreen from "../../components/widgets/UI/SongFullScreen";
import VideoBox from "../../components/widgets/UI/VideoBox";
import AudioBox from "../../components/widgets/UI/AudioBox";
import Spinner from "../../components/widgets/UI/Spinner";
import PresentationModal from "../../components/widgets/UI/PresentationModal";
import CopySong from "../../components/widgets/UI/CopySong";
import RestClient from "../../@core/restClient";
import { Auth } from "../../@core/auth";
import "../Songbook/Songbook.scss";

const BREAKPOINT = 992;
const RIGHT_BREAKPOINT = 1285;

const musicKey = [
  "Ab",
  "Abm",
  "A",
  "Am",
  "A#",
  "A#m",
  "Bb",
  "Bbm",
  "B",
  "Bm",
  "C",
  "Cm",
  "C#",
  "C#m",
  "Db",
  "Dbm",
  "D",
  "Dm",
  "D#",
  "D#m",
  "Eb",
  "Ebm",
  "E",
  "Em",
  "F",
  "Fm",
  "F#",
  "F#m",
  "Gb",
  "Gbm",
  "G",
  "Gm",
  "G#",
  "G#m"
];
const flatKey = [
  "C",
  "G",
  "D",
  "A",
  "E",
  "B",
  "Gb",
  "Db",
  "Ab",
  "Eb",
  "Bb",
  "F"
];
const sharpKey = [
  "C",
  "G",
  "D",
  "A",
  "E",
  "B",
  "F#",
  "C#",
  "G#",
  "D#",
  "A#",
  "F"
];
const chordCheck = [
  "Ab",
  "Eb",
  "Bb",
  "Gb",
  "Db",
  "F#",
  "C#",
  "G#",
  "D#",
  "A#",
  "F",
  "C",
  "G",
  "D",
  "A",
  "E",
  "B"
];

const DEFAULT_SONG = {
  title: "",
  subTitle: "",
  key: "",
  time: "",
  tempo: "",
  text: "",
  categories: [],
  files: []
};

const SongLibrary = () => {
  const { songId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [songs, setSongs] = useState<any[]>([]);
  const [songsLoading, setSongsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [song, setSong] = useState<any>(DEFAULT_SONG);
  const [isLoading, setIsLoading] = useState(false);
  const [screenMode, setScreenMode] = useState(false);
  const [forScreen, setForScreen] = useState<any[]>([]);
  const [noChords, setNoChords] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [fontSize, setFontSize] = useState(localStorage.getItem("font") || 1.8);
  const [openSidebar, setOpenSidebar] = useState({
    left: window.innerWidth >= BREAKPOINT,
    right: window.innerWidth >= RIGHT_BREAKPOINT
  });
  const [videoBoxOpen, setVideoBoxOpen] = useState(false);
  const [videoLink, setVideoLink] = useState("");
  const [audioBoxOpen, setAudioBoxOpen] = useState(false);
  const [audioLink, setAudioLink] = useState("");
  const [keyList, setKeyList] = useState(false);
  const [originalKey, setOriginalKey] = useState("");
  const [presentationModalOpen, setPresentationModalOpen] = useState(false);
  const [copySongOpen, setCopySongOpen] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [categoryList, setCategoryList] = useState<any[]>([]);

  const text = useRef() as React.MutableRefObject<HTMLDivElement>;
  const isLoadingMoreRef = useRef(false);
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(true);

  const check = /([A-G](#|b)?(m|maj|min|dim|aug|sus|add)?\d*(\/[A-G](#|b)?)?)+/;
  const exclude =
    /c|e|f|h|k|l|o|q|r|t|v|w|y|z|VERSE|CHORUS|TAG|VAMP|INTRO|BRIDGE|PRE/;
  const extraLines = /[*]/;
  const verseLine = /[%]/;
  const chorusLine = /[$]/;
  const bridgeLine = /[@]/;

  const categoryNameParam = searchParams.get("category") || "";
  const queryParam = searchParams.get("query") || "";
  const pageParam = searchParams.get("page") || "1";
  const searchQueryRulesParam = searchParams.get("searchQueryRules") || "";

  // Derive numeric category ID from name — only changes when the name or list actually changes,
  // so loading categoryList without a category selected never triggers a songs re-fetch.
  const categoryId = useMemo(() => {
    if (!categoryNameParam) return "";
    return (
      categoryList
        .find((c: any) => c.name === categoryNameParam)
        ?.id?.toString() || ""
    );
  }, [categoryNameParam, categoryList]);

  useEffect(() => {
    if (isLoadingMoreRef.current) return;
    // Wait until categoryList is ready before fetching with a category filter
    if (categoryNameParam && !categoryId) return;
    (async () => {
      setSongsLoading(true);
      try {
        const result = await RestClient.getSongListByQuery(
          queryParam,
          categoryId,
          true,
          parseInt(pageParam),
          200,
          searchQueryRulesParam || undefined
        );
        const sorted = [...result].sort(
          (a: any, b: any) => Number(b.id) - Number(a.id)
        );
        setSongs(sorted);
        setHasMore(result.length === 200);
        setPage(parseInt(pageParam));
      } catch (e) {
        console.log(e);
      } finally {
        setSongsLoading(false);
      }
    })();
  }, [categoryId, queryParam, pageParam, searchQueryRulesParam]);

  useEffect(() => {
    (async () => {
      try {
        const cats = await RestClient.getCategoryList();
        setCategoryList(cats || []);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  // Enrich song with songbook data from list (individual endpoint omits it)
  useEffect(() => {
    if (!song?.id || song?.songbook || !songs.length) return;
    const fromList = songs.find((s: any) => s.id === song.id);
    if (fromList?.songbook) {
      setSong((prev: any) => ({ ...prev, songbook: fromList.songbook }));
    }
  }, [songs, song?.id]);

  useEffect(() => {
    if (!songId) return;
    const urlKey = searchParams.get("key");
    const cached = songs.find((s: any) => s.id === Number(songId));
    if (cached && cached.text) {
      setSong(cached);
      setForScreen([cached]);
      setOriginalKey(cached.key || "");
      setNewKey(urlKey || cached.key || "");
      return;
    }
    getSong();
  }, [songId]);

  const getSong = async (ignoreKey = false) => {
    if (ignoreKey) setSearchParams({}, { replace: true });
    const urlKey = ignoreKey ? null : searchParams.get("key");
    try {
      setIsLoading(true);
      const data = await RestClient.getSong(songId!);
      setSong(data);
      setForScreen([data]);
      setOriginalKey(data.key || "");
      setNewKey(urlKey || data.key || "");
      if (urlKey) setSearchParams({ key: urlKey }, { replace: true });
    } catch (e) {
      console.log(e);
    }
    setIsLoading(false);
  };

  // Key transposition
  useEffect(() => {
    setIsLoading(true);
    const splitOriginal = originalKey.replace("m", "");
    const splitNewKey = newKey.replace("m", "");
    const startKey = splitOriginal.includes("#")
      ? sharpKey.indexOf(splitOriginal) + 1
      : flatKey.indexOf(splitOriginal) + 1;
    const endKey = splitNewKey.includes("#")
      ? sharpKey.indexOf(splitNewKey) + 1
      : flatKey.indexOf(splitNewKey) + 1;
    const shift =
      startKey > endKey
        ? flatKey.length - startKey + endKey
        : endKey - startKey;

    if (shift === 0) {
      setIsLoading(false);
      setOriginalKey(newKey);
      return;
    }

    Array.from(text.current?.children || []).forEach((item: any) => {
      const checked = item.innerHTML.match(check);
      const excluded = item.innerHTML.match(exclude);
      if (checked && excluded === null) {
        chordCheck.forEach((chord) => {
          const checkChord = new RegExp(`\\b${chord}(?![#b])`, "g");
          const checkKey = new RegExp("(D|E|G|A|B|C#|F#|G#|A#)(?![#b])", "g");
          if (item.innerHTML.match(checkChord)) {
            const oldI = chord.includes("b")
              ? flatKey.indexOf(chord)
              : sharpKey.indexOf(chord);
            const newChord = newKey.match(checkKey)
              ? `-${sharpKey[(oldI + shift) % 12]}`
              : `-${flatKey[(oldI + shift) % 12]}`;
            const re = new RegExp(`(?<!-)${chord}`, "g");
            item.innerHTML = item.innerHTML.replaceAll(re, newChord);
          }
        });
        item.innerHTML = item.innerHTML.replaceAll("-", "");
      }
    });
    setIsLoading(false);
    setOriginalKey(newKey);
  }, [newKey]);

  // Chord and comment styling
  useEffect(() => {
    Array.from(text.current?.children || []).forEach((item) => {
      const checked = item.innerHTML.match(check);
      const excluded = item.innerHTML.match(exclude);
      const comments = item.innerHTML.match(extraLines);
      const verse = item.innerHTML.match(verseLine);
      const chorus = item.innerHTML.match(chorusLine);
      const bridge = item.innerHTML.match(bridgeLine);
      if (noChords && checked && excluded === null) {
        item.classList.add("hide");
        return;
      } else {
        item.classList.remove("hide");
      }
      if (checked && !noChords && excluded === null) {
        item.classList.add("chords");
      } else {
        item.classList.remove("chords");
      }
      if (comments) {
        item.classList.add("comments");
      } else {
        item.classList.remove("comments");
      }
      if (comments && !showComments) {
        item.classList.add("hide");
      } else {
        item.classList.remove("hide");
      }
      if (verse) {
        item.classList.add("verse");
        item.innerHTML = item.innerHTML.replaceAll("%", "");
      } else if (chorus) {
        item.classList.add("chorus");
        item.innerHTML = item.innerHTML.replaceAll("$", "");
      } else if (bridge) {
        item.classList.add("bridge");
        item.innerHTML = item.innerHTML.replaceAll("@", "");
      }
    });
  }, [noChords, song, showComments]);

  useEffect(() => {
    const storedChords = localStorage.getItem("noChords");
    setNoChords(!!(storedChords && JSON.parse(storedChords)));
    const storedComments = localStorage.getItem("noComments");
    setShowComments(!!(storedComments && JSON.parse(storedComments)));
  }, []);

  const changeKey = (item: any) => {
    setKeyList(false);
    setNewKey(item);
    setSearchParams({ key: item }, { replace: true });
  };

  const resetKey = () => {
    setKeyList(false);
    setSearchParams({}, { replace: true });
    setNewKey(song.key);
  };

  const togleKeyList = (e: any) => {
    e.stopPropagation();
    setKeyList(!keyList);
  };

  useEffect(() => {
    if (keyList) {
      document.body.addEventListener("click", () => setKeyList(false));
    }
  }, [keyList]);

  const toggleChords = () => {
    setNoChords(!noChords);
    localStorage.setItem("noChords", JSON.stringify(!noChords));
  };

  const toggleComments = () => {
    setShowComments(!showComments);
    localStorage.setItem("noComments", JSON.stringify(!showComments));
  };

  const changeFontUp = () => {
    const newSize = Number(fontSize) + 0.1;
    setFontSize(newSize);
    localStorage.setItem("font", String(newSize));
  };

  const changeFontDown = () => {
    const newSize = Number(fontSize) - 0.1;
    setFontSize(newSize);
    localStorage.setItem("font", String(newSize));
  };

  const loadMore = async () => {
    const nextPage = page + 1;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    const next = new URLSearchParams(searchParams);
    next.set("page", nextPage.toString());
    setSearchParams(next);
    try {
      const result = await RestClient.getSongListByQuery(
        queryParam,
        categoryId,
        true,
        nextPage,
        200,
        searchQueryRulesParam || undefined
      );
      const sorted = [...result].sort(
        (a: any, b: any) => Number(b.id) - Number(a.id)
      );
      setSongs((prev) => [...prev, ...sorted]);
      setPage(nextPage);
      setHasMore(result.length === 200);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  };

  const goToPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", p.toString());
    setSearchParams(next);
  };

  const toggleSidebar = (side: "left" | "right", e: any) => {
    e.stopPropagation();
    setOpenSidebar({ ...openSidebar, [side]: !openSidebar[side] });
  };

  const openCopySong = () => setCopySongOpen(true);
  const closeCopySong = () => setCopySongOpen(false);

  const openVideoBox = (link: string) => {
    setVideoBoxOpen(true);
    setAudioBoxOpen(false);
    setVideoLink(link);
  };
  const closeVideoBox = () => setVideoBoxOpen(false);
  const openAudioBox = (link: string) => {
    setAudioBoxOpen(true);
    setVideoBoxOpen(false);
    setAudioLink(link);
  };
  const closeAudioBox = () => setAudioBoxOpen(false);

  useEffect(() => {
    if (window.innerWidth > 992) return;
    if (!openSidebar.left && !openSidebar.right) return;
    const handler = (e: MouseEvent) => {
      if (
        (e.target as Element).closest?.(
          ".left, .right, .toggler, .library-intro-arrow"
        )
      )
        return;
      setOpenSidebar((prev) => ({ ...prev, left: false, right: false }));
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [openSidebar.left, openSidebar.right]);

  useEffect(() => {
    const leftMedia = window.matchMedia(`(min-width: ${BREAKPOINT}px)`);
    const rightMedia = window.matchMedia(`(min-width: ${RIGHT_BREAKPOINT}px)`);
    const leftHandler = (e: MediaQueryListEvent) =>
      setOpenSidebar((prev) => ({ ...prev, left: e.matches }));
    const rightHandler = (e: MediaQueryListEvent) =>
      setOpenSidebar((prev) => ({ ...prev, right: e.matches }));
    leftMedia.addEventListener("change", leftHandler);
    rightMedia.addEventListener("change", rightHandler);
    return () => {
      leftMedia.removeEventListener("change", leftHandler);
      rightMedia.removeEventListener("change", rightHandler);
    };
  }, []);

  useEffect(() => {
    const content = document.getElementById("libraryBody");
    if (content) content.style.position = screenMode ? "fixed" : "relative";
  }, [screenMode]);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setTitleVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="songbook songbook--library">
      <Helmet>
        <title>
          {song?.title
            ? `${song.title} | ARYN Lyrics`
            : "Бібліотека християнських пісень - слова, акорди. | ARYN Lyrics"}
        </title>
        <meta
          name="description"
          content={
            song?.title
              ? `${song.title} — текст, слова, акорди, презентація. Бібліотека християнських пісень ARYN Lyrics.`
              : "Бібліотека християнських пісень - тексти, слова, акорди, презентація Power Point для кожної пісні."
          }
        />
        <meta
          name="keywords"
          content={
            song?.title
              ? `${song.title}, текст пісні, слова, акорди, презентація, ARYN Lyrics, Арін Лірікс, бібліотека пісень`
              : "бібліотека пісень, християнські пісні, тексти пісень, слова, акорди, ARYN Lyrics"
          }
        />
        <link rel="canonical" href={window.location.href} />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {screenMode && (
        <SongFullScreen
          selectedSongs={forScreen}
          closeFullScreen={() => setScreenMode(false)}
        />
      )}
      {!titleVisible && (
        <div className="sticky-title-bar sticky-title-bar--library">
          <div className="sticky-title-bar__toggler" onClick={(e) => toggleSidebar("left", e)}>
            {openSidebar.left ? <IoArrowBackCircleOutline /> : <IoArrowForwardCircleOutline />}
          </div>
          <span className="sticky-title-bar__title">{song?.title || "Бібліотека пісень"}</span>
          <div className="sticky-title-bar__toggler" onClick={(e) => toggleSidebar("right", e)}>
            {openSidebar.right ? <IoArrowForwardCircleOutline /> : <IoArrowBackCircleOutline />}
          </div>
        </div>
      )}
      <div className="title" ref={titleRef}>
        <Link to="/library" className="title-link">
          <h2>Бібліотека пісень</h2>
        </Link>
      </div>
      <div className="content-row" id="libraryBody">
        <div
          className={`toggler left_${openSidebar.left ? "open" : "close"}`}
          onClick={(e) => toggleSidebar("left", e)}
        >
          {openSidebar.left ? (
            <IoArrowBackCircleOutline />
          ) : (
            <IoArrowForwardCircleOutline />
          )}
        </div>
        <div className={`left ${openSidebar.left ? "open" : "close"}`}>
          <LeftSidebar
            isLibrary={true}
            librarySongs={songs}
            isLoading={songsLoading}
            closeSidebar={(e: any) => {
              if (window.innerWidth <= 1024) toggleSidebar("left", e);
            }}
            categoryList={categoryList}
            useCategories={true}
            songListModified={false}
            onSongsNumber={() => undefined}
            newSongId={null}
            songDeleted={false}
          />
          <div className="library-pagination">
            <button
              className="library-pagination-load-more"
              onClick={loadMore}
              disabled={!hasMore || isLoadingMore}
            >
              {isLoadingMore ? (
                <span className="library-pagination-spinner" />
              ) : (
                "Показати ще"
              )}
            </button>
            <div className="library-pagination-nav">
              {page > 1 && !isLoadingMore ? (
                <a
                  className="library-pagination-arrow"
                  href={`/library?page=${page - 1}`}
                  onClick={(e) => {
                    e.preventDefault();
                    goToPage(page - 1);
                  }}
                >
                  <IoArrowBackCircleOutline />
                </a>
              ) : (
                <span
                  className={`library-pagination-arrow${page <= 1 || isLoadingMore ? " disabled" : ""}`}
                >
                  <IoArrowBackCircleOutline />
                </span>
              )}
              <span className="library-pagination-page">{page}</span>
              {hasMore && !isLoadingMore ? (
                <a
                  className="library-pagination-arrow"
                  href={`/library?page=${page + 1}`}
                  onClick={(e) => {
                    e.preventDefault();
                    goToPage(page + 1);
                  }}
                >
                  <IoArrowForwardCircleOutline />
                </a>
              ) : (
                <span
                  className={`library-pagination-arrow${!hasMore || isLoadingMore ? " disabled" : ""}`}
                >
                  <IoArrowForwardCircleOutline />
                </span>
              )}
            </div>
          </div>
        </div>
        <div className={`center${!songId ? " center--intro" : ""}`}>
          {!songId && (
            <LibraryIntro recentSongs={songs} toggleSidebar={toggleSidebar} />
          )}
          {audioBoxOpen && songId && (
            <AudioBox audioLink={audioLink} closeAudioBox={closeAudioBox} />
          )}
          {songId && (
            <>
              <div className="tools">
                <div className="my-tooltip">
                  <button
                    className={
                      !showComments ? "chords-hide-btn" : "chords-show-btn"
                    }
                    onClick={toggleComments}
                  >
                    <IoChatbubbleEllipsesOutline />
                  </button>
                  <span className="my-tooltip-text tooltips-songbook">
                    {showComments ? "Сховати замітки" : "Показати замітки"}
                  </span>
                </div>
                <div className="my-tooltip">
                  <button
                    className={noChords ? "chords-hide-btn" : "chords-show-btn"}
                    onClick={toggleChords}
                  >
                    <IoMusicalNotesSharp />
                  </button>
                  <span className="my-tooltip-text tooltips-songbook">
                    {noChords ? "Показати акорди" : "Сховати акорди"}
                  </span>
                </div>
                <div className="my-tooltip">
                  <button
                    className="tool-icon"
                    onClick={() => setScreenMode(true)}
                  >
                    <IoDesktopOutline />
                  </button>
                  <span className="my-tooltip-text tooltips-songbook">
                    Повний екран
                  </span>
                </div>
                <div className="my-tooltip">
                  <button
                    className="tool-icon"
                    onClick={() => {
                      const shareUrl =
                        window.location.origin +
                        window.location.pathname +
                        (originalKey
                          ? `?key=${encodeURIComponent(originalKey)}`
                          : "");
                      if (navigator.share) {
                        navigator.share({ url: shareUrl });
                      } else {
                        navigator.clipboard.writeText(shareUrl);
                      }
                    }}
                  >
                    <IoShareSocialOutline />
                  </button>
                  <span className="my-tooltip-text tooltips-songbook">
                    Поділитись
                  </span>
                </div>
                {Auth.isAuthenticated ? (
                  <div className="my-tooltip">
                    <button className="tool-icon" onClick={openCopySong}>
                      <IoCopyOutline />
                    </button>
                    <span className="my-tooltip-text tooltips-songbook">
                      Копіювати
                    </span>
                  </div>
                ) : (
                  ""
                )}
              </div>
              <div className="change-font-size my-tooltip">
                Aa
                <div className="change-font-size-arrows">
                  <IoChevronUpOutline className="icon" onClick={changeFontUp} />
                  <IoChevronDownOutline
                    className="icon"
                    onClick={changeFontDown}
                  />
                </div>
                <span className="my-tooltip-text tooltips-songbook">
                  Розмір шрифта
                </span>
              </div>
              <div className="song">
                {isLoading || songsLoading ? <Spinner /> : ""}
                <p className="songTitle">{song?.title}</p>
                <p className="subtitle">
                  {song?.subTitle}
                  {song?.songbook?.title && (
                    <>
                      <br />
                      Пісенник: <span>{song.songbook.title}</span>
                    </>
                  )}
                </p>
                {!noChords && (
                  <div className="data">
                    <div className="key">
                      <p onClick={togleKeyList}>
                        Тональність:{" "}
                        <input
                          type="text"
                          className="input-box"
                          value={originalKey}
                          readOnly
                          onChange={() => undefined}
                        />
                        {keyList ? (
                          <IoChevronUpOutline className="key-icon" />
                        ) : (
                          <IoChevronDownOutline className="key-icon" />
                        )}
                      </p>
                      {keyList && (
                        <div className="key-select">
                          <p
                            className="key-select-item key-select-reset"
                            onClick={resetKey}
                          >
                            Original
                          </p>
                          {musicKey.map((item, i) => (
                            <p
                              className="key-select-item"
                              key={i}
                              onClick={() => changeKey(item)}
                            >
                              {item}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="time">
                      <p>
                        Розмір:<span>{song?.time}</span>
                      </p>
                    </div>
                    <div className="tempo">
                      <p>
                        Темп:<span>{song?.tempo}</span>
                      </p>
                    </div>
                  </div>
                )}
                <div className="text">
                  <div
                    style={{ fontSize: `${fontSize}rem` }}
                    ref={text}
                    className="text-inner"
                    dangerouslySetInnerHTML={{
                      __html: song?.text
                        ? draftToHtml(JSON.parse(song.text))
                        : ""
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        <div className={`right ${openSidebar.right ? "open" : "close"}`}>
          <RightSidebar
            songId={songId}
            onCreate={() => undefined}
            openDeleteSong={() => undefined}
            collabPermission={false}
            openFileLoadModal={() => undefined}
            fileLoadOpen={false}
            openVideoBox={openVideoBox}
            openAudioBox={openAudioBox}
            closeBothSideBars={() =>
              setOpenSidebar({ left: false, right: false })
            }
            mediaPermission={false}
            songFiles={song.files}
            presentationPermission={Auth.isAuthenticated}
            onCreatePresentation={() => setPresentationModalOpen(true)}
            isLibrary={true}
          />
        </div>
        <div
          className={`toggler right_${openSidebar.right ? "open" : "close"}`}
          onClick={(e) => toggleSidebar("right", e)}
        >
          {openSidebar.right ? (
            <IoArrowForwardCircleOutline />
          ) : (
            <IoArrowBackCircleOutline />
          )}
        </div>
      </div>
      {videoBoxOpen && (
        <VideoBox videoLink={videoLink} closeVideoBox={closeVideoBox} />
      )}
      {presentationModalOpen && (
        <PresentationModal
          song={song}
          onClose={() => setPresentationModalOpen(false)}
        />
      )}
      {copySongOpen ? (
        <CopySong closeCopySong={closeCopySong} song={song} />
      ) : (
        ""
      )}
    </div>
  );
};

export default SongLibrary;
